import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eraser, Skull } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { useAuth } from "@/context/AuthContext";
import { Panel } from "@/components/core/AppLayout";
import { DangerDialog } from "@/components/core/DangerDialog";

const futureWarn = (n) => (n ? `Attenzione: ci sono ${n} appuntamenti futuri con inviti già inviati. Verranno eliminati senza avvisare il cliente.` : null);

export const useClientDelete = (client, onClose) => {
  const navigate = useNavigate();
  const [state, setStateRaw] = useState(null);
  const setState = (v) => { setStateRaw(v); if (v === null) onClose?.(); };
  const open = async () => {
    setState({ items: null });
    try { setState((await api.get(`/clients/${client.id}/eliminazione`)).data); } catch (e) { toast.error(errMsg(e)); setState(null); }
  };
  const confirm = async (conferma) => {
    try { await api.delete(`/clients/${client.id}`, { params: { conferma } }); toast.success("Cliente spostato nel cestino: puoi ripristinarlo entro 7 giorni"); setState(null); navigate("/clienti"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const dialog = (
    <DangerDialog open={!!state} onOpenChange={(o) => !o && setState(null)} title="Eliminare il cliente?" items={state?.items ?? null}
      description={<>«{client.ragione_sociale}» e <strong>tutti</strong> i dati collegati (compresi gli appunti privati di tutti i soci) spariranno da ogni sezione e verranno spostati nel Cestino.</>}
      warning={futureWarn(state?.appuntamenti_futuri_invitati)} onConfirm={confirm} confirmLabel="Sposta nel cestino" irreversible={false} />
  );
  return { open, dialog };
};

export const ClientDeleteHost = ({ client, trigger, onClose }) => {
  const del = useClientDelete(client, onClose);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (trigger) del.open(); }, [trigger]);
  return del.dialog;
};

export const ClientDataPanel = ({ client }) => {
  const { user } = useAuth();
  const { data, reload } = useFetch(`/clients/${client.id}/sezioni`);
  const [sec, setSec] = useState(null);
  const del = useClientDelete(client);
  const clear = async (conferma) => {
    try { await api.post(`/clients/${client.id}/sezioni/${sec.key}/svuota`, { conferma }); toast.success(`Sezione ${sec.label} svuotata`); setSec(null); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6 mt-6 max-w-5xl">
      <Panel className="p-6" data-testid="client-data-panel">
        <h3 className="font-display text-lg font-extrabold text-[#0A1128] flex items-center gap-2 mb-1"><Eraser className="h-5 w-5 text-[#E8173A]" />Gestione dati del cliente</h3>
        <p className="text-sm text-slate-600 mb-4">Svuota una singola sezione. Per eliminare solo alcuni elementi usa le caselle di selezione negli elenchi.</p>
        <div className="grid grid-cols-2 gap-2">
          {(data?.sezioni || []).map((s) => (
            <div key={s.key} className="flex items-center justify-between rounded-xl bg-[#A6DAFC] px-4 py-2.5" data-testid={`section-row-${s.key}`}>
              <span className="font-bold text-[#0A1128]">{s.label} <span className="font-normal text-slate-600" data-testid={`section-count-${s.key}`}>({s.count})</span></span>
              <Button size="sm" variant="outline" disabled={!s.count} onClick={() => setSec(s)} className="rounded-full font-bold text-[#C8102E] border-red-200" data-testid={`section-clear-${s.key}`}>Svuota</Button>
            </div>
          ))}
        </div>
      </Panel>
      {user.role === "admin" && (
        <Panel className="p-6 border-2 !border-red-300" data-testid="client-danger-zone">
          <h3 className="font-display text-lg font-extrabold text-[#C8102E] flex items-center gap-2 mb-1"><Skull className="h-5 w-5" />Zona pericolosa</h3>
          <p className="text-sm text-slate-700 mb-4">Sposta nel Cestino il cliente e tutti i dati collegati: documenti, task, PED, appuntamenti, accessi, portale, report, campagne, email, fatture e appunti privati. Dal Cestino (Gestione dati) puoi ripristinarlo entro 7 giorni.</p>
          <Button onClick={del.open} className="rounded-full bg-[#C8102E] hover:bg-[#A50D26] font-bold" data-testid="client-delete-all-button">Elimina cliente e tutti i dati</Button>
        </Panel>
      )}
      <DangerDialog open={!!sec} onOpenChange={(o) => !o && setSec(null)} title={`Svuotare la sezione ${sec?.label}?`} items={sec?.items || []}
        description={`Cliente: ${client.ragione_sociale}`} warning={sec?.key === "appuntamenti" ? futureWarn(data?.appuntamenti_futuri_invitati) : null} onConfirm={clear} confirmLabel="Svuota sezione" />
      {del.dialog}
    </div>
  );
};
